import type { ThunkDispatch, UnknownAction } from '@reduxjs/toolkit';
import { useCallback, useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';

/**
 * Dispatch that also accepts thunks. The state type is left open on purpose:
 * lower layers must not import the store, which sits at the top of the app,
 * and each thunk states the slice of state it needs itself.
 */
// eslint-disable-next-line @typescript-eslint/no-explicit-any
export type AppDispatch = ThunkDispatch<any, unknown, UnknownAction>;
export const useAppDispatch = useDispatch.withTypes<AppDispatch>();

/** Whether the browser currently reports a network connection. */
export function useOnline(): boolean {
  const [online, setOnline] = useState(() => navigator.onLine);
  useEffect(() => {
    const update = () => setOnline(navigator.onLine);
    window.addEventListener('online', update);
    window.addEventListener('offline', update);
    return () => {
      window.removeEventListener('online', update);
      window.removeEventListener('offline', update);
    };
  }, []);
  return online;
}

/**
 * True once `active` has stayed true for `ms` without a break. For notices about a
 * state that is normal when brief (a request in flight) and worth a word only when
 * it lasts: shown at once, they flash on every request.
 */
export function useHeld(active: boolean, ms: number): boolean {
  const [elapsed, setElapsed] = useState(false);
  useEffect(() => {
    if (!active) return;
    const timer = setTimeout(() => setElapsed(true), ms);
    return () => {
      clearTimeout(timer);
      setElapsed(false);
    };
  }, [active, ms]);
  return active && elapsed;
}

function readStored<T>(key: string, accept: (value: unknown) => value is T): T | undefined {
  try {
    const raw = localStorage.getItem(key);
    if (raw === null) return undefined;
    const value: unknown = JSON.parse(raw);
    return accept(value) ? value : undefined;
  } catch {
    return undefined; // unreadable or blocked storage: as if nothing was kept
  }
}

/**
 * State that this browser remembers between visits: a preference of one viewer, such
 * as which parts of a list are folded. Nothing else depends on it, so storage that is
 * blocked, full or holding something else simply gives `fallback`. A new `key` reads
 * what is kept under it.
 */
export function useStoredState<T>(
  key: string,
  fallback: T,
  accept: (value: unknown) => value is T,
): [T, (value: T) => void] {
  const [state, setState] = useState(() => ({ key, value: readStored(key, accept) ?? fallback }));
  let current = state;
  if (state.key !== key) {
    current = { key, value: readStored(key, accept) ?? fallback };
    setState(current);
  }
  const set = useCallback(
    (value: T) => {
      setState({ key, value });
      try {
        localStorage.setItem(key, JSON.stringify(value));
      } catch {
        // Not remembered, but still in effect for this visit.
      }
    },
    [key],
  );
  return [current.value, set];
}
